import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  createContext
} from "react";
import { withRouter } from "react-router-dom";
import { withApollo } from "react-apollo";
import _ from "lodash";
import {
  SUBSCRIPTION,
  GET_USER_SUBSCRIPTIONS,
  PAYMENT,
  GET_PAYMENTS,
  VALIDATE_COUPON,
  VALIDATE_ORDER_DISCOUNT_CODE
} from "./queries";
import { GET_SUBSCRIPTION_FEE } from "../../pages/Shared/Checkout/queries";
import { AuthContext } from "../AuthProvider";
import { MessageContext } from "../MessageContext";

export const CartContext = createContext();
export const defaultLicenseType = "Podcast";
export const defaultLevel = "Standard";
export const defaultBillingInterval = "year";

const CartProvider = ({ children, client, location }) => {
  const levelCoefficients = { Basic: 1, Standard: 2, Professional: 4 };

  const auth = useContext(AuthContext);
  const { showMessage } = useContext(MessageContext);

  const [cartItems, setCartItems] = useState([]);
  const [orderId, setOrderId] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [total, setTotal] = useState(0);
  const [payments, setPayment] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [licenseType, setLicenseType] = useState(defaultLicenseType);
  const [level, setLevel] = useState(defaultLevel);
  const [purchasing, setPurchasing] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [discountCode, setDiscountCode] = useState(null);
  const [currentSubscriptionPlan, setCurrentSubscriptionPlan] = useState(null);
  const [coupon, setCoupon] = useState(null);
  // When true (set from a ?trial=true checkout link), request a 30-day Stripe free
  // trial. The backend only honors it for first-time subscribers.
  const [trialRequested, setTrialRequested] = useState(false);
  const [billingInterval, setBillingInterval] = useState(
    defaultBillingInterval
  );
  const [allPlans, setAllPlans] = useState({ month: {}, year: {} });

  // If user logs out, set selected project to null.
  useEffect(() => {
    setSelectedProject(null);
  }, [auth.user.role]);

  // update plan details when subscription level or billing interval changes (regular subscriptions)
  useEffect(() => {
    const isSubscription = currentSubscriptionPlan !== null;
    if (isSubscription && level in subscriptionPlans) {
      const subscriptionPlan = subscriptionPlans[level];
      setCurrentSubscriptionPlan({
        planId: subscriptionPlan.planId,
        subscriptionFee: subscriptionPlan.price,
        interval: subscriptionPlan.interval,
        intervalCount: subscriptionPlan.intervalCount
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionPlans, level]);

  // When billingInterval or allPlans changes, switch the active subscriptionPlans
  // and bootstrap currentSubscriptionPlan if it hasn't been set yet
  useEffect(() => {
    const plans = allPlans[billingInterval] ?? {};
    if (Object.keys(plans).length > 0) {
      setSubscriptionPlans(plans);

      // Bootstrap currentSubscriptionPlan on first load (when it's still null)
      if (currentSubscriptionPlan === null && plans[level]) {
        const plan = plans[level];
        setCurrentSubscriptionPlan({
          planId: plan.planId,
          subscriptionFee: plan.price,
          interval: plan.interval,
          intervalCount: plan.intervalCount
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingInterval, allPlans]);

  // Any coupon we resolved was specific to the previously-selected billing
  // interval (interval-tagged Stripe coupons share marketing names but live
  // under distinct IDs). When the user flips the toggle, drop the stale code
  // so the next checkout doesn't quietly apply the wrong one.
  useEffect(() => {
    if (coupon) {
      setCoupon(null);
      setDiscountCode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingInterval]);

  /*
   * Calculates cart total and updates
   * Considers subscription plan, level, cart items, discount codes
   *
   * Note: if subscription selected, don't add cart items to the total, the blanket
   *       subscription covers everything.
   */
  const updateTotal = useCallback(() => {
    let totalPrice = 0;

    // blanket subscriptions cover everything, so we don't need to calculate cart items in total
    if (currentSubscriptionPlan) {
      totalPrice = currentSubscriptionPlan.subscriptionFee;
    } else {
      if (cartItems.length > 0) {
        cartItems.forEach(item => {
          // Coerce defensively so a missing/invalid price can't turn the whole
          // total into NaN. We don't fabricate a price here — album price is now
          // fetched in GET_ALBUM, so a bad value is unexpected; treat it as 0
          // and let the checkout amount guard keep <Elements> renderable.
          totalPrice += Number(item.price) || 0;
        });
      }
    }

    // consider level
    if (!currentSubscriptionPlan) totalPrice *= levelCoefficients[level] ?? 1;

    // consider discounts
    if (discountCode !== null && discountCode.valid) {
      if (discountCode.amount_off !== null) {
        totalPrice -= discountCode.amount_off;
      }
      if (discountCode.percent_off !== null) {
        totalPrice *= Math.max(0, 1 - discountCode.percent_off / 100.0);
      }
    }

    const roundedTotal = Math.round(totalPrice * 100) / 100;
    setTotal(roundedTotal);
    return roundedTotal;
  }, [
    currentSubscriptionPlan,
    cartItems,
    levelCoefficients,
    level,
    discountCode
  ]);

  // update pricing whenever cart items, subscription, discount code, or level changes
  useEffect(() => {
    updateTotal();
  }, [cartItems, currentSubscriptionPlan, discountCode, level, updateTotal]);

  /*
   * Rehydrate cart details from local storage
   * Cart items stored in local storage so it persists across session, user can come back to cart
   */
  useEffect(() => {
    // project
    const project = localStorage.getItem("selectedProject");
    setSelectedProject(JSON.parse(project));

    // cart items
    const items = JSON.parse(localStorage.getItem("cartItems")) ?? [];
    setCartItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Fetches payments made by the user
   */
  const fetchPayments = async () => {
    try {
      const res = await client.query({
        query: GET_PAYMENTS,
        fetchPolicy: "network-only"
      });
      setPayment(res.data.payments);
    } catch (err) {
      console.error(err);
    }
  };

  /*
   * Fetches subscriptions made by the user.
   */
  const fetchUserSubscriptions = async () => {
    try {
      const res = await client.query({
        query: GET_USER_SUBSCRIPTIONS,
        variables: { userId: auth.user.id },
        fetchPolicy: "network-only"
      });
      setSubscriptions(res.data.userSubscriptions);
    } catch (err) {
      console.error(err);
    }
  };

  /*
   * Fetch subscription plan details
   */
  const fetchSubscriptionPlans = async callback => {
    try {
      const { data } = await client.query({
        query: GET_SUBSCRIPTION_FEE
      });

      // Partition plans by interval (month vs year)
      // Setting allPlans triggers the useEffect chain:
      //   allPlans → subscriptionPlans → currentSubscriptionPlan
      const plansByInterval = { month: {}, year: {} };
      data.getPrice.forEach(item => {
        const interval = item.interval || "month";
        if (!plansByInterval[interval]) plansByInterval[interval] = {};
        plansByInterval[interval][item.license] = { ...item };
      });

      setAllPlans(plansByInterval);

      if (callback) {
        const activePlans =
          plansByInterval[billingInterval] ?? plansByInterval.month;
        callback(activePlans);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const removeCartItem = async cartItem => {
    const updated = cartItems.filter(item => item.id !== cartItem.id);
    localStorage.setItem("cartItems", JSON.stringify(updated));
    setCartItems(updated);
  };

  const addCartItem = async cartItem => {
    const updated = [
      ...cartItems,
      {
        ...cartItem,
        itemId: cartItem.id,
        name: cartItem.title || cartItem.name,
        type: cartItem.stem ? "Track" : "Album"
      }
    ];
    localStorage.setItem("cartItems", JSON.stringify(updated));
    setCartItems(updated);
  };

  /*
   * Gets price at checkout as a display string
   *
   * For orders: ${total}
   * For subscriptions: ${total}/${intervalCount} ${interval}
   */
  const getPriceString = ({ showBilledTotal = false } = {}) => {
    const displayTotal = parseInt(total) === total ? total : total.toFixed(2);
    if (currentSubscriptionPlan) {
      if (currentSubscriptionPlan.interval === "year") {
        if (showBilledTotal) {
          return `$${displayTotal} / year`;
        }
        // Display as monthly rate with annual billing note elsewhere
        const monthlyRate = total / 12;
        const displayMonthly =
          parseInt(monthlyRate) === monthlyRate
            ? monthlyRate
            : monthlyRate.toFixed(2);
        return `$${displayMonthly} / month`;
      }
      return `$${displayTotal} / ${
        currentSubscriptionPlan.intervalCount > 1
          ? currentSubscriptionPlan.intervalCount + " "
          : ""
      }month${currentSubscriptionPlan.intervalCount > 1 ? "s" : ""}`;
    } else {
      // create order price string
      return `$${displayTotal}`;
    }
  };

  /**
   * Returns a billing clarification string for annual plans,
   * e.g. "billed $480 yearly". Returns null for monthly plans.
   */
  const getAnnualBillingNote = () => {
    if (!currentSubscriptionPlan || currentSubscriptionPlan.interval !== "year")
      return null;
    const displayTotal = parseInt(total) === total ? total : total.toFixed(2);
    return `billed $${displayTotal} yearly`;
  };

  /**
   * Returns a "first charge vs. ongoing" disclosure when an active discount
   * makes the first invoice cheaper than the renewal price — e.g.
   *   { today: "$0.00 today", ongoing: "then $50/mo after the first month" }
   *
   * Returns null when there's nothing to disclose (no subscription, no
   * discount, "forever" discounts, malformed repeating coupons, or no
   * price delta).
   *
   * Best-practice rationale: any time the recurring charge will differ from
   * what the user is about to authorize, we surface that delta so customers
   * can't be surprised by a renewal. We describe the timing relatively
   * ("after the first month") rather than with an absolute date because the
   * authoritative renewal timestamp lives on the Stripe subscription.
   */
  const getIntroPricingNote = () => {
    if (!currentSubscriptionPlan || !discountCode || !discountCode.valid)
      return null;
    if (!["once", "repeating"].includes(discountCode.duration)) return null;

    const fullPrice = currentSubscriptionPlan.subscriptionFee;
    if (!fullPrice || total >= fullPrice) return null;

    const interval =
      currentSubscriptionPlan.interval === "year" ? "year" : "month";
    const intervalShort = interval === "year" ? "/yr" : "/mo";

    const fmt = n =>
      Number.isInteger(n) ? `$${n}` : `$${Number(n).toFixed(2)}`;

    // Describe when full-price billing kicks in. We deliberately avoid an
    // absolute date here because the renewal timestamp lives on the Stripe
    // subscription and any client-computed date will drift from the email
    // the customer eventually receives.
    let when;
    if (discountCode.duration === "once") {
      when =
        interval === "year" ? "after the first year" : "after the first month";
    } else {
      // repeating: discount applies for N months, then renews at full price.
      // A malformed coupon without duration_in_months is unsafe to guess at,
      // so skip the disclosure entirely.
      const months = discountCode.duration_in_months;
      if (!months) return null;
      // On an annual plan the first full-price charge isn't until the next
      // renewal a year out, so a month-count ("after 6 months") would be
      // misleading even when the coupon's own duration is measured in months.
      when =
        interval === "year"
          ? "after the first year"
          : `after the introductory ${months} ${
              months === 1 ? "month" : "months"
            }`;
    }

    return {
      today: `${fmt(total)} today`,
      ongoing: `then ${fmt(fullPrice)}${intervalShort} ${when}`
    };
  };

  const isFoundInCart = cartItem => {
    return cartItems.find(item => item.itemId === cartItem.id);
  };

  /*
   * Apply discount code to the cart for orders
   *
   * Arguments:
   *   Code: discount code value
   */
  const applyOrderDiscountCode = async code => {
    try {
      const { data } = await client.query({
        query: VALIDATE_ORDER_DISCOUNT_CODE,
        variables: {
          discountCode: code.trim()
        }
      });
      if (data.discount === null) throw new Error("Invalid Discount Code");

      // the discount code is valid, apply it
      setCoupon(code);
      setDiscountCode({
        id: data.discount.id,
        name: code,
        amount_off: null,
        duration: "once",
        duration_in_months: null,
        percent_off: data.discount.percent,
        valid: true
      });

      showMessage({
        type: "success",
        duration: 5000,
        title: "Discount Applied",
        description: `We applied discount code ${code} for ${data.discount.percent}% off!`
      });
    } catch (err) {
      showMessage({
        type: "error",
        duration: 5000,
        title: "Error",
        description:
          "We failed to validate your discount code. Please try again. "
      });
    }
  };

  /*
   * Remove the discount code
   */
  const removeDiscountCode = async () => {
    setCoupon(null);
    setDiscountCode(null);
  };

  /*
    description: discount code for subscription
    arguments:
      discountCode: discount code value
  */
  const applySubscriptionDiscountCode = async discountCode => {
    if (!currentSubscriptionPlan) return;

    try {
      const { data } = await client.query({
        query: VALIDATE_COUPON,
        variables: {
          name: discountCode.trim(),
          interval: billingInterval
        }
      });

      const subscriptionDiscount = data.subscriptionDiscount;
      if (subscriptionDiscount && subscriptionDiscount.valid) {
        // Store the resolved Stripe coupon ID (not the user-typed string) so
        // that subscription creation can hand it straight to Stripe. The
        // typed string may be a marketing-facing name shared by several
        // interval-specific coupons; only the ID is unambiguous.
        setCoupon(subscriptionDiscount.id);
        setDiscountCode(subscriptionDiscount);
        showMessage({
          type: "success",
          duration: 5000,
          title: "Discount Applied",
          description: `We applied discount code ${discountCode.trim()} for ${
            subscriptionDiscount.percent_off
              ? `${subscriptionDiscount.percent_off}%`
              : `$${subscriptionDiscount.amount_off}`
          } off.`
        });
      } else {
        showMessage({
          type: "error",
          duration: 5000,
          title: "Error",
          description: "Invalid discount code. Please try again."
        });
      }
    } catch (err) {
      showMessage({
        type: "error",
        duration: 5000,
        title: "Error",
        description: "Invalid discount code. Please try again."
      });
    }
  };

  /*
   * Creates a pending order and its Stripe PaymentIntent. Returns the raw orderPayment
   * result ({ id, clientSecret, status }) to CheckoutForm, which owns confirmation,
   * finalization, and post-payment UX. Throws on failure.
   */
  const payForOrder = async formValues => {
    // this flow is for orders only, not subscriptions
    if (currentSubscriptionPlan !== null) return;

    // prepare form data for payment API call
    // if orderId=null, create a new order, otherwise try to look up as custom order to fulfill it
    const orderData = {
      ...formValues,
      email: auth.user.email ? auth.user.email : formValues.email,
      orderId: orderId,
      level: level,
      licenseType: licenseType,
      price: total,
      items: cartItems.map(item => ({
        id: item.id,
        type: item.type,
        price: item.price
      })),
      discountCode: discountCode?.name
    };
    if (formValues.sameName) orderData.nameOnLicense = "";
    delete orderData.sameName;
    setProjectId(orderData.projectId);

    const { data, errors } = await client.mutate({
      mutation: PAYMENT,
      variables: orderData
    });
    if (errors) {
      throw new Error(errors[0].message);
    }

    if (!data.orderPayment) {
      throw new Error("Payment failed.");
    }

    // Return the raw result; CheckoutForm confirms the PaymentIntent and handles all
    // post-payment UX. Don't navigate/empty the cart here — the charge isn't complete
    // until the client confirms.
    return data.orderPayment;
  };

  /*
   * Initiates a Stripe subscription. Returns the raw result ({ id, clientSecret,
   * status, intentType }) to CheckoutForm, which confirms the intent and handles
   * navigation + analytics. Throws on failure.
   */
  const payForSubscription = async (formValues, id) => {
    if (!currentSubscriptionPlan) return;

    const formData = _.omit(formValues, "sameName");
    setProjectId(formData.projectId);

    const { data, errors } = await client.mutate({
      mutation: SUBSCRIPTION,
      variables: {
        ...formData,
        planId: currentSubscriptionPlan.planId,
        type: level,
        licenseType: licenseType,
        coupon: coupon,
        trial: trialRequested,
        subId: id
      }
    });
    if (errors) {
      throw new Error(errors[0].message);
    }

    if (!data.subscription?.id) {
      // we weren't able to form a subscription for some reason, throw general error
      throw new Error("Payment failed");
    }

    // Flag to trigger the project setup modal after signup / landing on Browse.
    localStorage.setItem("showProjectSetupModal", "true");

    // Return the raw result; intentType tells CheckoutForm whether to confirmPayment or
    // confirmSetup.
    return data.subscription;
  };

  /*
    description: after we're done making a purchase we reset the data to their 
    initial values.
  */
  const cleanUpCart = async () => {
    localStorage.removeItem("cartItems");
    setOrderId(null);
    setSubscriptionId(null);
    setCartItems([]);
    setLevel(defaultLevel);
    setLicenseType(defaultLicenseType);
    setBillingInterval(defaultBillingInterval);
    setCurrentSubscriptionPlan(null);
    setDiscountCode(null);
    setTrialRequested(false);
    setTotal(0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        setCartItems,
        addCartItem,
        removeCartItem,
        total,
        setTotal,
        getPriceString,
        getAnnualBillingNote,
        getIntroPricingNote,
        isFoundInCart,
        applyOrderDiscountCode,
        applySubscriptionDiscountCode,
        discountCode,
        setDiscountCode,
        removeDiscountCode,
        licenseType,
        setLicenseType,
        level,
        purchasing,
        setPurchasing,
        setLevel,
        cleanUpCart,
        payments,
        payForOrder,
        payForSubscription,
        subscriptions,
        updateTotal,
        currentSubscriptionPlan,
        setCurrentSubscriptionPlan,
        orderId,
        setOrderId,
        subscriptionId,
        setSubscriptionId,
        fetchPayments,
        projectId,
        fetchSubscriptionPlans,
        fetchUserSubscriptions,
        setSubscriptionPlans,
        subscriptionPlans,
        allPlans,
        billingInterval,
        setBillingInterval,
        trialRequested,
        setTrialRequested,
        selectedProject,
        setSelectedProject,
        location
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default withRouter(withApollo(CartProvider));
